import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RefreshButton from '../../../components/fields/buttons/refresh-button';
import TableTab from "../../../components/tabs/table-tab";
import { FILTER_DATE } from "../../../constants/filter-types";
import { REPORTS_TRANSACTION_ALARM } from "../../../constants/menu-types";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import TransactionColumn from "../../../models/ColumnDefinitions/TransactionColumn";
import { Filter } from "../../../models/Filter";
import { ListData } from "../../../models/ListData";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import BuildingSearch from "../../../models/search/BuildingSearch";
import CompanySearch from "../../../models/search/CompanySearch";
import ControllerSearch from "../../../models/search/ControllerSearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import DoorSearch from "../../../models/search/DoorSearch";
import EventTypeSearch from "../../../models/search/EventTypeSearch";
import ReaderSearch from "../../../models/search/ReaderSearch";
import TransactionSearch from "../../../models/search/TransactionSearch";
import ZoneSearch from "../../../models/search/ZoneSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import { getBuildingNamesWithPagination } from "../../../services/buildingService";
import { getCompanyNamesWithPagination } from "../../../services/companyService";
import { getControllerNamesWithPagnation } from "../../../services/controllerService";
import { getDepartmentNamesWithPagination } from "../../../services/departmentService";
import { getDoorNamesWithPagination } from "../../../services/doorService";
import { getEventTypeNamesWithPagination } from "../../../services/eventTypeService";
import { getReaderNamesWithPagination } from "../../../services/readerService";
import { getTransactionReport } from "../../../services/reportService";
import { getUserFiltersByView } from "../../../services/userPreferenceService";
import { getZoneNamesWithPagination } from "../../../services/zoneService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { IAppState } from "../../../store/types";
import { getTodayDate } from "../../../utilities/DateUtils";
import { appendFilter } from "../../../utilities/filters";
import { hideColumns } from "../../../models/utilities/columnHelper";
import { auditRunReport } from "../../../services/auditService";
import { getErrorMessage } from "../../../services/utilities/ServiceError";

interface AlarmTransactionsProps {
    reportId: string;
}

const AlarmTransactions: FC<AlarmTransactionsProps> = ({ reportId }) => {
    const { translate } = useLocale();
    const [refreshReport, setRefreshReport] = useState(true);
    const user = useSelector((state: IAppState) => (state.userInfo));
    const lookup: Lookup[] = [
        {
            name: LookupTypes.eventType,
            values: [],
            dataMethod: (listQuery?: ListQuery) =>
                getEventTypeNamesWithPagination({
                    ...listQuery,
                    filters: appendFilter(1, 'Alarm Events', 'etAlarm', listQuery?.filters ?? [])
                }),
            searchAccessor: EventTypeSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.location,
            values: [],
            dataMethod: getDoorNamesWithPagination,
            searchAccessor: DoorSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.zone,
            values: [],
            dataMethod: getZoneNamesWithPagination,
            searchAccessor: ZoneSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {

            name: LookupTypes.building,
            values: [],
            dataMethod: getBuildingNamesWithPagination,
            searchAccessor: BuildingSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.controller,
            values: [],
            dataMethod: getControllerNamesWithPagnation,
            searchAccessor: ControllerSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.terminal,
            values: [],
            dataMethod: getReaderNamesWithPagination,
            searchAccessor: ReaderSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(column => column.accessor === "name")?.searchOnName
        }
    ];

    useEffect(() => {
        if (refreshReport) {
            dataRefresh(getDataMethod)
        }
        setRefreshReport(false);
    }, [refreshReport])

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = REPORTS_TRANSACTION_ALARM;
    const initialiseUserPreferences = (setDetails: (userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys) => void) => {
        // Filters that should always show for this view
        const staticFilterKeys = ["dateOnly"];

        //Add custom filter
        let customFilter: Filter[] = [];
        getUserFiltersByView(userLoginId, currentMenuDetail, reportId).then((response) => {
            const dateFilter = response.find(filter => filter.filterId === FILTER_DATE);
            if (!!!dateFilter) {
                customFilter = [{
                    isCustomFilter: true,
                    key: "dateOnly",
                    label: translate.byKey(TranslationKeys.date),
                    options: [],
                    selected: [{
                        label: getTodayDate(),
                        value: getTodayDate(),
                    }]
                }];
            }
        }).catch((error) => {
            console.log("Failed to load user preferences")
        }).finally(() => {
            setDetails(userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys);
        })
    }
    // <-- end context

    const getReportFilters = (currentFilters) => {

        // Default to today's date
        let filters = appendFilter(`*${getTodayDate()}*`, "Date/Time", "dateOnly", currentFilters);

        // Limit to alarm type transactions only.
        return appendFilter('2', 'Transack Type', 'transackType.id', filters);
    }

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getTransactionReport(user.profileId, translate, { ...listParameters, filters: getReportFilters(listParameters?.filters) });
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    }

    return (<TableContextProvider
        dataMethod={getDataMethod}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        manualDataRefresh={manualDataRefresh}
        tableDescriptor={hideColumns(TransactionColumn, ["name"])}
        searchDescriptor={TransactionSearch}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.transactionsAlarms)}
            selectRows={false}
            showFilterMenu={true}
            cyName="alarmTransactions">
            <RefreshButton onRefresh={setRefreshReport} />
        </TableTab>
    </TableContextProvider >
    );
};

export default AlarmTransactions;
